import { Component, EventEmitter, Inject, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval } from 'rxjs';
import { ApiUrlHelper } from '../../../common/api-url-helper';
import { CommonService } from '../../../services/common/common.service';
import { ChangePasswordSuccessPopup } from '../../../component/change-password-success-popup/change-password-success-popup';
import { CommonErrorMessages } from '../../../constants/ErrorMessages';

@Component({
  selector: 'app-change-password-verification-dialog',
  standalone: false,
  templateUrl: './change-password-verification-dialog.html',
  styleUrl: './change-password-verification-dialog.scss'
})
export class ChangePasswordVerificationDialog {

  uniqueId: string;
  emailId: string;
  oldPassword: any;
  newPassword: any;
  confirmNewPassword: any;
  userId: number;
  otp: any;
  userDetails: any;
  submitted: boolean = false;
  verificationForm: FormGroup | any;
  isTimerRunning: boolean = false;
  isResendDisabled: boolean = true;
  private subscription: Subscription = new Subscription();
  counter = 301;
  tick = 1000;
  isDisabled: boolean = true;
  isCounterShow: boolean = true;
  minutes: any = 5;
  seconds: any = 0;
  @ViewChild('ngOtpInput', { static: false }) ngOtpInput: any;
  @Output() viewVerificationCode = new EventEmitter<boolean>();
  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<ChangePasswordVerificationDialog>,
    private readonly fb: FormBuilder,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly commonService: CommonService,
    private readonly api: ApiUrlHelper,
    private readonly dialog: MatDialog
  ) {
    this.uniqueId = data.uniqueId;
    this.userId = data.userId;
    this.emailId = data.emailId;
    this.oldPassword = data.oldPassword;
    this.newPassword = data.newPassword;
    this.confirmNewPassword = data.confirmNewPassword;
  }

  ngOnInit(): void {
    this.verificationForm = this.fb.group({
      verificationCode: ['', [Validators.required]]
    });

    localStorage.removeItem('otpEndTimeForChangePassword');
    this.isResendDisabled = true;
    this.resetTimer();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onOtpChange(event: any) {
    this.otp = event;
  }

  verifyCode() {
    this.submitted = true;
    if (this.otp == null || this.otp == undefined || this.otp == '') {
      this.submitted = false;
      this.toastr.error(CommonErrorMessages.AuthenticationCodeRequired);
      return
    }

    let obj = {
      OTP: this.otp,
      UserId: this.userId,
      NewPassword: this.newPassword,
      UniqueId: this.uniqueId,
    }
    const apiUrl = this.api.apiUrl.Account.VerificationForChangePassword;
    this.spinner.show();
    this.commonService.doPost(apiUrl, obj).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.spinner.hide();

          const dialogRef = this.dialog.open(ChangePasswordSuccessPopup, {
            width: '500px',
            disableClose: true
          });
          dialogRef.afterClosed().subscribe();
        }
        else {
          this.toastr.error(response.message);
          this.otp = '';
          this.ngOtpInput.setValue('');
          this.submitted = false;
        }
        this.spinner.hide();
      },
      error: (err) => {
        console.error(err);
        this.spinner.hide();
        this.submitted = false;
      },
    });
  }

  ResendOTP() {
    if (this.isResendDisabled) return; // Prevent early clicks
    this.isResendDisabled = true;
    this.resetTimer(); // Start fresh timer
    this.spinner.show();
    this.loadTimer();
    this.otp = '';
    this.ngOtpInput.setValue('');
    const apiUrl = this.api.apiUrl.Account.ChangePassword;
    let obj = {
      OldPassword: this.oldPassword,
      NewPassword: this.newPassword,
      ConfirmPassword: this.confirmNewPassword,
    };
    this.commonService.doPost(apiUrl, obj).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.submitted = false;
          this.spinner.hide();
          this.toastr.success(response.message);
        } else {
          this.spinner.hide();
          this.toastr.error(response.message);
        }
      },
      error: (err) => {
        console.error(err);
        this.spinner.hide();
      },
    });
  }

  loadTimer() {
    const storedEndTime = localStorage.getItem('otpEndTimeForChangePassword');
    if (storedEndTime) {
      const endTime = new Date(storedEndTime).getTime();
      const currentTime = new Date().getTime();
      const remainingTime = Math.floor((endTime - currentTime) / 1000);
      if (remainingTime > 0) {
        this.counter = remainingTime;
        this.startTimer();
      } else {
        this.handleTimerEnd();
      }
    } else {
      this.resetTimer();
    }
  }

  saveTimer() {
    const endTime = new Date().getTime() + this.counter * 1000;
    localStorage.setItem('otpEndTimeForChangePassword', new Date(endTime).toISOString());
  }

  resetTimer() {
    this.counter = 301;
    this.saveTimer();
    this.startTimer();
  }

  startTimer() {
    this.subscription?.unsubscribe();
    this.subscription = interval(1000).subscribe(() => {
      if (this.counter === 0) {
        this.handleTimerEnd();
        this.subscription?.unsubscribe();
      } else {
        this.counter--;
        this.minutes = Math.floor(this.counter / 60).toString().padStart(2, '0');
        this.seconds = (this.counter % 60).toString().padStart(2, '0');
        this.saveTimer();
      }
    });
  }

  handleTimerEnd() {
    this.subscription.unsubscribe();
    this.verificationForm.disable();
    this.submitted = true;
    this.isResendDisabled = false;
    localStorage.removeItem('otpEndTimeForChangePassword');
  }

  close() {
    this.dialogRef.close(false);
  }
}